import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPaw,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faHeart
} from '@fortawesome/free-solid-svg-icons';
import {
  faTwitter,
  faFacebook,
  faInstagram,
  faLinkedin
} from '@fortawesome/free-brands-svg-icons';

function Footer() {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    company: [
      { label: 'About Us', href: '/about' },
      { label: 'Our Mission', href: '/mission' },
      { label: 'Careers', href: '/careers' },
      { label: 'Press', href: '/press' },
    ],
    services: [
      { label: 'Pet Matching', href: '/quiz' },
      { label: 'Veterinarians', href: '/services' },
      { label: 'Pet Sitting', href: '/services#sitting' },
      { label: 'Training', href: '/services#training' },
    ],
    support: [
      { label: 'Help Center', href: '/help' },
      { label: 'Contact Us', href: '/contact' },
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' },
    ],
  };

  const socialLinks = [
    { icon: faFacebook, href: 'https://facebook.com/pawfectmatch', label: 'Facebook' },
    { icon: faTwitter, href: 'https://twitter.com/pawfectmatch', label: 'Twitter' },
    { icon: faInstagram, href: 'https://instagram.com/pawfectmatch', label: 'Instagram' },
    { icon: faLinkedin, href: 'https://linkedin.com/company/pawfectmatch', label: 'LinkedIn' },
  ];

  return (
    <footer 
      className="bg-neutral-900 dark:bg-neutral-950 text-neutral-300"
      role="contentinfo"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <Link 
              to="/" 
              className="flex items-center space-x-2 text-primary-300 hover:text-primary-200 transition-colors mb-4"
              aria-label="Pawfect Match - Go to homepage"
            >
              <FontAwesomeIcon icon={faPaw} className="h-8 w-8" />
              <span className="text-xl font-bold">Pawfect Match</span>
            </Link>
            <p className="text-sm text-neutral-400 mb-4 max-w-xs">
              Connecting pets with their perfect families through intelligent matching 
              and comprehensive care services.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-2 text-sm">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="h-4 w-4 text-primary-400" />
                <span>San Francisco, CA</span>
              </div>
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faPhone} className="h-4 w-4 text-primary-400" />
                <a 
                  href="tel:+1-555-PAWFECT" 
                  className="hover:text-primary-300 transition-colors"
                  aria-label="Call us at 1-555-PAWFECT"
                >
                  1-555-PAWFECT
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faEnvelope} className="h-4 w-4 text-primary-400" />
                <a 
                  href="mailto:hello@pawfectmatch.com" 
                  className="hover:text-primary-300 transition-colors"
                  aria-label="Email us at hello@pawfectmatch.com"
                >
                  hello@pawfectmatch.com
                </a>
              </div>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Company</h3>
            <ul className="space-y-2">
              {footerLinks.company.map(link => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-neutral-400 hover:text-primary-300 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-neutral-900 rounded"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Services</h3>
            <ul className="space-y-2">
              {footerLinks.services.map(link => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-neutral-400 hover:text-primary-300 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-neutral-900 rounded"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Support</h3>
            <ul className="space-y-2">
              {footerLinks.support.map(link => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-neutral-400 hover:text-primary-300 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-neutral-900 rounded"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-neutral-800 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Copyright */}
            <div className="text-sm text-neutral-400">
              <p>
                © {currentYear} Pawfect Match. All rights reserved. Made with{' '}
                <FontAwesomeIcon icon={faHeart} className="h-4 w-4 text-red-500 mx-1" />
                for pets and their families.
              </p>
            </div>

            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks.map(social => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-neutral-400 hover:text-primary-300 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-neutral-900 rounded"
                  aria-label={`Follow us on ${social.label}`}
                >
                  <FontAwesomeIcon icon={social.icon} className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;